import {FC, useState} from "react";
import {DatePicker, DateTimePicker, Drop, Input, Type as T} from "@infobis/ui-styles-utils";
import {ApplicationDataState} from "../../types";
import st from './formBody.module.scss';
import {maxStrValidator, minNumValidator} from "../../../../../components/applicationForm/utils";
import {GeoZoneHashElement} from "@context/map/fields/fieldsStore";
import {Operation} from "../../../../../../../store/reducers/references/operations";
import {Good} from "../../../../../../../store/reducers/partners/goods";

interface FormBodyProps {
    item: ApplicationDataState
    actions: any
    options: {
        fields: GeoZoneHashElement[]
        filteredOperations: Operation[]
        filteredGoods: Good[]
        square?: string
    }
    container: HTMLDivElement
};

const INN_VALIDATOR = Input.Validator(...minNumValidator(12));
const PHONE_VALIDATOR = Input.Validator(...minNumValidator(11));

const STR_VALIDATOR = Input.Validator(...maxStrValidator(255));
const COMMENT_VALIDATOR = Input.Validator(...maxStrValidator(1000));
const FormBody: FC<FormBodyProps> = ({item, actions, options, container}) => {

    const {fields, filteredOperations, filteredGoods, square} = options;

    return (
        <>
            <div className={st['multiple-inputs']}>
                <DatePicker
                    inputProps={{
                        label: 'Желаемая дата начала работ',
                        style: {width: '301px'},
                        required: true
                    }}
                    dateFormat={'dd.MM.yyyy'}
                    value={item.startDate || undefined}
                    onChange={(d) => actions.setStartDate(d as [Date])}
                    portalProps={{container}}
                />
                <DatePicker
                    inputProps={{
                        label: 'Желаемая дата завершения работ',
                        style: {width: '301px'},
                        required: true
                    }}
                    dateFormat={'dd.MM.yyyy'}
                    value={item.finishDate || undefined}
                    onChange={(d) => actions.setFinishDate(d as [Date])}
                    portalProps={{container}}
                />
            </div>
            <div className={st['multiple-inputs']}>
                <Drop.Select
                    className={st['multiple-inputs__item']}
                    label={'Поле'}
                    width={T.Width.FULL}
                    options={fields}
                    value={item.polygonJson as GeoZoneHashElement | undefined}
                    customOptions={(c) => ({
                        label: c.name,
                        glid: c.glid,
                    })}
                    uniq={'glid'}
                    onChange={(v) => actions.setPolygonJson(v)}
                    pickerFieldProps={{portalProps: {container}}}
                    required
                />
                <Input.Text
                    className={st['multiple-inputs__item']}
                    readOnly
                    label={'Площадь обработки, Га'}
                    width={T.Width.FULL}
                    value={square}
                    placeholder={'Выберите поле'}
                />
            </div>
            <div className={st['multiple-inputs']}>
                <Drop.Select
                    className={st['multiple-inputs__item']}
                    label={'Вид работ'}
                    width={T.Width.FULL}
                    options={filteredOperations}
                    value={item.jobVid || undefined}
                    customOptions={(c) => ({
                        label: c.title,
                        id: c.id,
                    })}
                    onChange={(v) => actions.setJobVid(v)}
                    pickerFieldProps={{
                        portalProps: {
                            container,
                            offsets: {y: 0, x: 5},
                            flip: true
                        }
                    }}
                    required
                />
                <Drop.Select
                    className={st['multiple-inputs__item']}
                    label={'Средство защиты растений'}
                    width={T.Width.FULL}
                    options={filteredGoods}
                    value={item.szr || undefined}
                    customOptions={(c) => ({
                        label: c.title,
                        id: c.id,
                    })}
                    onChange={(v) => actions.setSzr(v)}
                    pickerFieldProps={{portalProps: {container}}}
                    required
                />
            </div>
            <Input.Text
                validators={Input.useValidator(STR_VALIDATOR.fn!, STR_VALIDATOR.message)}
                label={'Имя заказчика'}
                width={T.Width.FULL}
                value={item.customerName}
                onChange={actions.setCustomerName}
                required
            />
            <div className={st['multiple-inputs']}>
                <Input.Text
                    validators={Input.useValidator(STR_VALIDATOR.fn!, STR_VALIDATOR.message)}
                    className={st['multiple-inputs__item']}
                    label={'Реквизиты заказчика'}
                    width={T.Width.FULL}
                    value={item.customerRekv}
                    onChange={actions.setCustomerRekv}
                    required
                />
                <Input.Mask
                    validators={Input.useValidator(INN_VALIDATOR.fn!, INN_VALIDATOR.message)}
                    maskProps={{mask: "999999999999", maskPlaceholder: null}}
                    className={st['multiple-inputs__item']}
                    label={'ИНН заказчика'}
                    width={T.Width.FULL}
                    value={item.customerInn}
                    onChange={actions.setCustomerInn}
                    required
                />
            </div>
            <div className={st['multiple-inputs']}>
                <Input.Mask
                    validators={Input.useValidator(PHONE_VALIDATOR.fn!, PHONE_VALIDATOR.message)}
                    maskProps={{mask: "+7 999 999 99 99", maskPlaceholder: null}}
                    className={st['multiple-inputs__item']}
                    label={'Телефон'}
                    width={T.Width.FULL}
                    value={item.customerTlf}
                    onChange={actions.setCustomerTlf}
                    placeholder={'Введите номер телефона'}
                    required
                />
                <Input.Text
                    validators={Input.useValidator(STR_VALIDATOR.fn!, STR_VALIDATOR.message)}
                    className={st['multiple-inputs__item']}
                    label={'E-Mail'}
                    width={T.Width.FULL}
                    value={item.customerEmail}
                    onChange={actions.setCustomerEmail}
                    validateType={'email'}
                    required
                />
            </div>
            <div className={st['multiple-inputs']}>
                <DateTimePicker
                    inputProps={{
                        label: 'Дата передачи СЗР',
                        style: {width: '301px'},
                        required: true
                    }}
                    dateFormat={'dd.MM.yyyy, HH:mm'}
                    value={item.szrExchangeTime || undefined}
                    onChange={(d) => actions.setSzrExchangeTime(d as [Date])}
                    portalProps={{container}}
                />
                <Input.NumberFloat
                    maskProps={undefined}
                    label={'Желаемая стоимость работ'}
                    width={T.Width.FULL}
                    value={item.price!}
                    onChange={(v) => actions.setPrice(Number(v))}
                    decimals={3}
                    numberType={'float'}
                />
            </div>
            <Input.Text
                validators={Input.useValidator(STR_VALIDATOR.fn!, STR_VALIDATOR.message)}
                label={'Описания места передачи СЗР'}
                width={T.Width.FULL}
                value={item.szrExchangePlace}
                onChange={actions.setSzrExchangePlace}
                required
            />
            <Input.Text
                validators={Input.useValidator(STR_VALIDATOR.fn!, STR_VALIDATOR.message)}
                label={'Произвольное кодовое слово (указывается заказчиком для подтверждения договоренности между ним и эксплуатантом)'}
                width={T.Width.FULL}
                value={item.keyWord}
                onChange={actions.setKeyWord}
                required
            />
            <Input.Text
                validators={Input.useValidator(COMMENT_VALIDATOR.fn!, COMMENT_VALIDATOR.message)}
                label={'Комментарий'}
                width={T.Width.FULL}
                value={item.comment}
                onChange={actions.setComment}
            />
        </>
    );
};

export default FormBody;